import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/api/client';
import { useAuth, type AuthenticatedUser } from './AuthContext';

/**
 * Iter-8 (P0+P1) follow-up. The unauth-chain LanguageSelector already calls
 * `i18n.changeLanguage(lang)` and the i18next browser detector persists it
 * to localStorage under `yusr.lang`. But that choice was never PATCHed up
 * to the server, so a fresh login from another device would render in the
 * server-stored language instead of the user's chosen one.
 *
 * This hook closes that gap — once the user is authenticated, if the local
 * i18n language differs from `user.language`, it PATCHes
 * `/api/users/me/preferences` once and updates the in-memory user via
 * setUser. Idempotent, network-failure-tolerant.
 */
export function useSyncPreferredLanguage(): void {
  const { user, status, setUser } = useAuth();
  const { i18n } = useTranslation();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated' || !user) return;

    const local = (i18n.resolvedLanguage ?? i18n.language) as AuthenticatedUser['language'];
    if (!['ar', 'en', 'ur'].includes(local)) return;
    if (local === user.language) return;
    if (syncedFor.current === user.id + ':' + local) return;
    syncedFor.current = user.id + ':' + local;

    void api()
      .patch('/users/me/preferences', { language: local })
      .then(() => setUser({ ...user, language: local }))
      .catch(() => {
        // Soft failure — user keeps their local language; will retry on next mount.
        syncedFor.current = null;
      });
  }, [status, user, i18n.resolvedLanguage, i18n.language, setUser]);
}
